import http from 'hub-http/clients/apiClient';
import { dataFetchingClient } from '../../utils/DataFetchingClient';
import { registerMutation, registerQuery, unwrapHubHttpError, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import getLang from 'LearningCenterUI/utils/getLang';
import { FILTER_CONSTS } from '../../constants/TopFilterConstants';
import { useCallback, useMemo } from 'react';
import { updateCache } from '../../utils/DataFetchingClientCache';
import maxBy from 'hs-lodash/maxBy';
import findIndex from 'hs-lodash/findIndex';
import orderBy from 'hs-lodash/orderBy';
import { useFullTrack } from '../tracks/tracksQueries';
import { getPlaylistIdFromUrl } from '../../utils/UrlParamUtils';
import { useGetUserProgressQuery } from '../../../__generated__/chirp/com/hubspot/userprogress/bff/rpc/UserProgressBFFChirpService.hooks';
/** Registered queries */

export const GET_ALL_PROGRESS = registerQuery({
  fieldName: 'allProgress',
  args: ['language'],
  fetcher({
    language = getLang()
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/user/${language}`, {
      timeout: 25000
    }).then(response => response);
  }
});
const GET_TRACK_PROGRESS = registerQuery({
  fieldName: 'trackProgress',
  args: ['trackId'],
  fetcher({
    trackId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/track/${trackId}`).then(response => response);
  }
});
const GET_LESSON_PROGRESS = registerQuery({
  fieldName: 'lessonProgress',
  args: ['lessonId'],
  fetcher({
    lessonId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/lesson/${lessonId}`).then(response => response);
  }
});
export const GET_PLAYLIST_PROGRESS = registerQuery({
  fieldName: 'playlistProgress',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.get(`${BASE_URI_PROGRESS}/user-progress/playlist/${playlistId}`).then(response => {
      const progress = response;
      return Object.assign({}, progress, {
        itemsProgress: orderBy(progress.itemsProgress, ['updatedAt'], ['desc'])
      });
    });
  }
});
export const usePlaylistProgress = (playlistId, skip) => {
  const {
    data: playlistProgress,
    loading: playlistProgressLoading,
    error: playlistProgressError
  } = useQuery(GET_PLAYLIST_PROGRESS, {
    variables: {
      playlistId
    },
    skip
  });
  return {
    playlistProgress,
    playlistProgressLoading,
    playlistProgressError
  };
};
export const UPDATE_CONTENT_PROGRESS = registerMutation({
  fieldName: 'updateContentProgress',
  args: ['contentId', 'trackId', 'lessonId', 'playlistId', 'language'],
  fetcher({
    contentId,
    trackId,
    lessonId,
    playlistId,
    language
  }) {
    return http.post(`${BASE_URI_PROGRESS}/user-progress/content/${contentId}`, {
      data: {
        trackId,
        lessonId,
        playlistId,
        language
      }
    });
  }
});

/** Cache updator functions */

export const deleteContentProgressFromCache = (contentId, language = getLang()) => {
  updateCache(GET_ALL_PROGRESS, allProgress => {
    delete allProgress.contentProgress[contentId];
  }, {
    language
  });
};
export const setContentProgressStatusCache = (contentId, status, language = getLang()) => {
  updateCache(GET_ALL_PROGRESS, allProgress => {
    allProgress.contentProgress[contentId] = {
      duration: 180,
      // Not sure where this is coming from, possibly a magic number
      updatedAt: new Date().getTime(),
      completed: status === 'COMPLETED',
      type: status
    };
  }, {
    language
  });
};
export const setMostRecentExamAttemptCache = (trackId, attemptData) => {
  updateCache(GET_TRACK_PROGRESS, trackProgress => {
    trackProgress.examAttempts[attemptData.id] = attemptData;
    trackProgress.examIdToRecentAttemptId[attemptData.examId] = attemptData.id;
  }, {
    trackId
  });
};
export const addQuestionSubmissionCache = (trackId, attemptId, questionId, answerIds) => {
  updateCache(GET_TRACK_PROGRESS, trackProgress => {
    trackProgress.examAttempts[attemptId].questionSubmissions = [...trackProgress.examAttempts[attemptId].questionSubmissions, {
      questionId,
      answerIds
    }];
  }, {
    trackId
  });
};
export const setLessonQuizAttemptCache = (lessonId, quizId, attemptData) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    lessonProgress.quizIdToRecentAttemptId[quizId] = attemptData.id;
    lessonProgress.quizAttempts[attemptData.id] = attemptData;
  }, {
    lessonId
  });
};
export const clearLessonQuizAttemptCache = (lessonId, quizId, attemptId) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    delete lessonProgress.quizIdToRecentAttemptId[quizId];
    delete lessonProgress.quizAttempts[attemptId];
  }, {
    lessonId
  });
};
export const addLessonQuizAttemptAnswerCache = (lessonId, attemptId, questionId, answerIds, correctAnswer) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    lessonProgress.quizAttempts[attemptId].answerSubmissions.push({
      quizAttemptId: attemptId,
      id: 0,
      questionId,
      answerIds,
      correctAnswer,
      submissionTime: new Date().getTime()
    });
  }, {
    lessonId
  });
};
export const addLessonStartQuizAttemptAnswerCache = (lessonId, attemptId, questionId, answerIds, correctAnswer, startQuizInfo) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    lessonProgress.quizIdToRecentAttemptId[startQuizInfo.quizId] = attemptId;
    lessonProgress.quizAttempts[attemptId] = Object.assign({}, startQuizInfo.firstAttemptData, {
      answerSubmissions: [...startQuizInfo.firstAttemptData.answerSubmissions, {
        quizAttemptId: attemptId,
        id: 0,
        questionId,
        answerIds,
        correctAnswer,
        submissionTime: new Date().getTime()
      }]
    });
  }, {
    lessonId
  });
};
export const removeLessonQuizAttemptAnswerCache = (lessonId, attemptId, questionId) => {
  updateCache(GET_LESSON_PROGRESS, lessonProgress => {
    lessonProgress.quizAttempts[attemptId].answerSubmissions = lessonProgress.quizAttempts[attemptId].answerSubmissions.filter(answer => answer.questionId !== questionId);
  }, {
    lessonId
  });
};

/** Core query hooks */

export const useAllProgress = (variables, options = {}) => {
  const {
    language
  } = variables || {};
  const {
    data: allProgress,
    loading: allProgressLoading,
    error: allProgressError
  } = useQuery(GET_ALL_PROGRESS, {
    variables: {
      language: language || getLang()
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    context: {
      timeout: options.timeout || 25000
    }
  });
  return {
    allProgress: allProgress || {
      assetProgress: [],
      contentProgress: {}
    },
    allProgressLoading,
    allProgressError
  };
};
export const useShortProgressById = shortId => {
  const {
    allProgress,
    allProgressLoading
  } = useAllProgress();
  const shortProgressObject = allProgress.assetProgress.find(({
    id,
    type
  }) => id === shortId && type === 'SHORT_FORM_CONTENT');
  return {
    shortStatus: shortProgressObject === null || shortProgressObject === void 0 ? void 0 : shortProgressObject.status,
    shortStatusLoading: allProgressLoading
  };
};
export const useTrackProgressById = trackId => {
  const {
    data: trackProgressById,
    loading: trackProgressByIdLoading,
    error: trackProgressByIdError,
    refetch: trackProgressByIdRefetch
  } = useQuery(GET_TRACK_PROGRESS, {
    variables: {
      trackId: trackId
    },
    skip: !trackId || isNaN(Number(trackId)),
    onCompleted(data) {
      data.trackProgress.progress.forEach(lessonProgress => {
        updateCache(GET_LESSON_PROGRESS, () => lessonProgress, {
          lessonId: lessonProgress.lessonId
        });
      });
    }
  });
  return {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError,
    trackProgressByIdRefetch
  };
};
export const useLessonProgressById = lessonId => {
  const {
    data: lessonProgressById,
    loading: lessonProgressByIdLoading,
    error: lessonProgressByIdError,
    refetch: lessonProgressByIdRefetch
  } = useQuery(GET_LESSON_PROGRESS, {
    variables: {
      lessonId: Number(lessonId)
    },
    skip: !lessonId
  });
  return {
    lessonProgressById,
    lessonProgressByIdLoading,
    lessonProgressByIdError,
    lessonProgressByIdRefetch
  };
};
export const START_PLAYLIST_PROGRESS = registerMutation({
  fieldName: 'startPlaylistProgress',
  args: ['playlistId'],
  fetcher({
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/playlist/${playlistId}`);
  }
});
export const useStartPlaylistProgress = ({
  playlistId,
  language
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startPlaylistProgress, {
    loading: startPlaylistProgressLoading,
    called: startPlaylistProgressCalled,
    error: startPlaylistProgressCalledError
  }] = useMutation(START_PLAYLIST_PROGRESS, {
    variables: {
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])]
  });
  return [startPlaylistProgress, {
    startPlaylistProgressLoading,
    startPlaylistProgressCalled,
    startPlaylistProgressCalledError
  }];
};
export const START_TRACK_PROGRESS = registerMutation({
  fieldName: 'startTrackProgress',
  args: ['language', 'trackId', 'playlistId'],
  fetcher({
    language,
    trackId,
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/track/${trackId}`, {
      data: {
        playlistId,
        language
      }
    });
  }
});
export const useStartTrackProgress = ({
  language,
  trackId,
  playlistId
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startTrackProgress, {
    loading: startTrackProgressLoading,
    called: startTrackProgressCalled,
    error: startTrackProgressError
  }] = useMutation(START_TRACK_PROGRESS, {
    variables: {
      language,
      trackId: trackId,
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, {
      query: GET_TRACK_PROGRESS,
      variables: {
        trackId
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])]
  });
  return [startTrackProgress, {
    startTrackProgressLoading,
    startTrackProgressCalled,
    startTrackProgressError
  }];
};
export const START_LESSON_PROGRESS = registerMutation({
  fieldName: 'startLessonProgress',
  args: ['language', 'lessonId', 'playlistId'],
  fetcher({
    language,
    lessonId,
    playlistId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/asset-progress/start/lesson/${lessonId}`, {
      data: {
        playlistId,
        language
      }
    });
  }
});
export const useStartLessonProgress = ({
  language,
  lessonId,
  playlistId
}) => {
  const playlistIdToUse = playlistId || getPlaylistIdFromUrl();
  const [startLessonProgress, {
    loading: startLessonProgressLoading,
    called: startLessonProgressCalled,
    error: startLessonProgressError
  }] = useMutation(START_LESSON_PROGRESS, {
    variables: {
      language,
      lessonId: lessonId,
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, {
      query: GET_LESSON_PROGRESS,
      variables: {
        lessonId
      }
    }, ...(playlistIdToUse ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId: playlistIdToUse
      }
    }] : [])]
  });
  return [startLessonProgress, {
    startLessonProgressLoading,
    startLessonProgressCalled,
    startLessonProgressError
  }];
};
export const useUpdateContentProgress = () => {
  const [mutate, {
    loading: updateContentProgressLoading,
    called: updateContentProgressCalled,
    error: updateContentProgressError,
    data: updateContentProgressData
  }] = useMutation(UPDATE_CONTENT_PROGRESS);
  const updateContentProgress = useCallback(({
    contentId,
    lessonId,
    trackId,
    language
  }) => {
    const playlistId = getPlaylistIdFromUrl();
    return mutate({
      awaitRefetchQueries: true,
      variables: {
        contentId,
        lessonId,
        trackId,
        playlistId,
        language
      },
      refetchQueries: [{
        query: GET_ALL_PROGRESS,
        variables: {
          language
        },
        context: {
          timeout: 25000
        }
      }, ...(lessonId ? [{
        query: GET_LESSON_PROGRESS,
        variables: {
          lessonId
        }
      }] : []), ...(trackId ? [{
        query: GET_TRACK_PROGRESS,
        variables: {
          trackId
        }
      }] : []), ...(playlistId ? [{
        query: GET_PLAYLIST_PROGRESS,
        variables: {
          playlistId
        }
      }] : [])]
    });
  }, [mutate]);
  return [updateContentProgress, {
    updateContentProgressLoading,
    updateContentProgressCalled,
    updateContentProgressError,
    updateContentProgressData
  }];
};
export const updateContentProgress = ({
  contentId,
  lessonId,
  trackId,
  language
}) => {
  const playlistId = getPlaylistIdFromUrl();
  return dataFetchingClient.mutate({
    mutation: UPDATE_CONTENT_PROGRESS,
    variables: {
      contentId,
      lessonId,
      trackId,
      language,
      playlistId
    },
    refetchQueries: [{
      query: GET_ALL_PROGRESS,
      variables: {
        language
      },
      context: {
        timeout: 25000
      }
    }, ...(lessonId ? [{
      query: GET_LESSON_PROGRESS,
      variables: {
        lessonId
      }
    }] : []), ...(trackId ? [{
      query: GET_TRACK_PROGRESS,
      variables: {
        trackId
      }
    }] : []), ...(playlistId ? [{
      query: GET_PLAYLIST_PROGRESS,
      variables: {
        playlistId
      }
    }] : [])]
  }).then(result => result.data);
};

/** Derived query hooks */

export const useProgressAssetsByStatus = (variables, options = {}) => {
  const {
    status,
    context
  } = variables;
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress(null, {
    fetchPolicy: options.fetchPolicy
  });
  return {
    assets: allProgress.assetProgress.filter(asset => {
      let keepCriteria = asset.status === status;
      if (context && asset.type !== 'PLAYLIST') {
        keepCriteria = keepCriteria && asset.context === context;
      }
      return keepCriteria;
    }),
    allProgressLoading,
    allProgressError
  };
};
export const useAssetStatus = ({
  id,
  type,
  language,
  skip
}) => {
  let status = 'NOT_STARTED';
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress({
    language
  }, {
    skip
  });
  if (!allProgressLoading) {
    const progress = allProgress.assetProgress.find(asset => asset.type === type && asset.id === id && (!language || asset.language === language));
    if (progress) {
      status = progress.status;
    }
  }
  return {
    status,
    allProgressLoading,
    allProgressError
  };
};
export const useContentProgress = (id, language) => {
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress({
    language
  });
  return {
    contentProgress: allProgress ? allProgress.contentProgress[id] : undefined,
    contentProgressLoading: allProgressLoading,
    contentProgressError: allProgressError
  };
};
export const useContentStatus = (id, language) => {
  const {
    contentProgress,
    contentProgressLoading,
    contentProgressError
  } = useContentProgress(id, language);
  let contentStatus = 'NOT_STARTED';
  if (contentProgress) {
    contentStatus = contentProgress.type;
  }
  return {
    contentStatus,
    contentStatusLoading: contentProgressLoading,
    contentStatusError: contentProgressError
  };
};
export const useAssetTimeRemaining = ({
  id,
  type,
  contentIds,
  totalTime
}) => {
  let remainingTime = totalTime;
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress();
  const {
    status
  } = useAssetStatus({
    id,
    type
  });
  if (contentIds && allProgress && status !== FILTER_CONSTS.STATUSES.NOT_STARTED) {
    remainingTime = contentIds.reduce((calculatingTime, contentId) => {
      if (allProgress.contentProgress[Number(contentId)] && allProgress.contentProgress[Number(contentId)].completed) {
        return calculatingTime - allProgress.contentProgress[Number(contentId)].duration;
      }
      return calculatingTime;
    }, totalTime);
  }
  return {
    remainingTime,
    allProgressLoading,
    allProgressError
  };
};
export const useRemainingLessons = ({
  lessonIds = []
}) => {
  const totalLessons = lessonIds.length;
  const {
    allProgress,
    allProgressLoading,
    allProgressError
  } = useAllProgress();
  const remainingLessons = lessonIds.reduce((prevRemainingLessons, lessonId) => {
    const lessonComplete = allProgress.assetProgress.find(asset => Number(asset.id) === Number(lessonId) && asset.type === 'LESSON' && asset.status === 'COMPLETED');
    if (lessonComplete) {
      return prevRemainingLessons - 1;
    }
    return prevRemainingLessons;
  }, totalLessons);
  return {
    totalLessons,
    remainingLessons,
    allProgressLoading,
    allProgressError
  };
};
export const useLastLessonProgressById = lessonId => {
  let lastProgressedContentId;
  const {
    lessonProgressById,
    lessonProgressByIdLoading,
    lessonProgressByIdError
  } = useLessonProgressById(lessonId);
  if (lessonProgressById && lessonProgressById.contentProgress.length) {
    lastProgressedContentId = lessonProgressById.contentProgress[lessonProgressById.contentProgress.length - 1].contentId;
  }
  return {
    lastProgressedContentId,
    lessonProgressByIdLoading,
    lessonProgressByIdError
  };
};
export const useTrackRedirectUrl = (trackId, language) => {
  const {
    trackProgressById,
    trackProgressByIdLoading,
    trackProgressByIdError
  } = useTrackProgressById(Number(trackId));
  const {
    track,
    trackLoading,
    trackError
  } = useFullTrack(trackId, language);
  const trackRedirectUrl = useMemo(() => {
    if (!trackProgressById || !track) return null;
    const allContentProgress = [];
    if (track.introVideo) {
      allContentProgress.push({
        lessonId: null,
        contentId: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.contentId,
        status: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.progressType,
        updatedAt: trackProgressById.introVideoProgress && trackProgressById.introVideoProgress.updatedAt,
        url: `/tracks/${trackId}/intro`
      });
    }
    track.lessons.forEach(lesson => {
      var _trackProgressById$pr;
      const lessonProgress = (_trackProgressById$pr = trackProgressById.progress) === null || _trackProgressById$pr === void 0 ? void 0 : _trackProgressById$pr.find(l => l.lessonId === lesson.id);
      if (lessonProgress) {
        lesson.content.forEach(content => {
          const contentProgress = lessonProgress.contentProgress.find(c => c.contentId === content.id);
          allContentProgress.push({
            lessonId: lesson.id,
            contentId: content.id,
            status: contentProgress && lessonProgress.status !== 'NOT_STARTED' ? contentProgress.progressType : 'NOT_STARTED',
            updatedAt: contentProgress ? contentProgress.updatedAt : null,
            url: `/tracks/${trackId}/${lesson.id}/${content.id}`
          });
        });
      }
    });
    allContentProgress.push({
      lessonId: null,
      contentId: null,
      status: null,
      updatedAt: null,
      url: `/tracks/${trackId}/${track.exam ? 'exam' : 'complete'}`
    });
    if (trackProgressById.status === 'COMPLETED') {
      return allContentProgress[allContentProgress.length - 1].url;
    }
    const lastCompleted = maxBy(allContentProgress.filter(p => p.status === 'COMPLETED'), p => p.updatedAt);
    if (lastCompleted) {
      const lastCompletedIndex = findIndex(allContentProgress, lastCompleted);
      const unprogressedItemsAfterLastCompletedItem = allContentProgress.slice(lastCompletedIndex + 1).filter(p => p.status !== 'COMPLETED');
      return unprogressedItemsAfterLastCompletedItem[0].url;
    }
    return allContentProgress[0].url;
  }, [track, trackId, trackProgressById]);
  return {
    trackRedirectUrl,
    trackRedirectUrlLoading: trackProgressByIdLoading || trackLoading,
    trackRedirectUrlError: trackProgressByIdError || trackError
  };
};
export const useSetContentProgressStatusCache = ({
  language
} = {
  language: getLang()
}) => {
  const {
    allProgressLoading
  } = useAllProgress({
    language
  });
  return {
    setContentProgressStatusCache,
    allProgressLoading
  };
};
export const useTrackProgress = (trackId, options = {}) => {
  const {
    data: userProgressData,
    loading,
    error
  } = useGetUserProgressQuery({
    variables: {
      requestedProgress: [{
        id: trackId,
        type: 'TRACK'
      }]
    },
    fetchPolicy: options.fetchPolicy,
    pollInterval: options.polling ? 1000 : 0,
    skip: options.skip,
    context: {
      timeout: options.timeout
    }
  });
  const trackProgress = userProgressData === null || userProgressData === void 0 ? void 0 : userProgressData.progress.find(progress => progress.type === 'TRACK');
  return {
    trackProgress,
    trackProgressLoading: loading,
    trackProgressError: unwrapHubHttpError(error)
  };
};